/**
 * Load-time preset validation.
 *
 * Per the plan: unknown warp.kind, unknown palette, unknown modulation
 * targets, sources, or transforms must fail loudly with clear errors and a
 * list of valid alternatives. No silent no-ops.
 */

import {
  AUDIO_SOURCES,
  MODULATION_TARGETS,
  TRANSFORM_KINDS,
  WarpKind,
  type AudioSource,
  type ModulationBinding,
  type ModulationTarget,
  type TransformKind,
  type VisualizerPreset,
  type WarpFieldConfig,
} from './presetTypes';
import { WARP_FIELD_GENERATORS } from '../warpField/types';

export class PresetValidationError extends Error {
  constructor(
    public readonly presetId: string,
    public readonly issues: string[],
  ) {
    super(
      `Preset "${presetId}" failed validation:\n  - ${issues.join('\n  - ')}`,
    );
    this.name = 'PresetValidationError';
  }
}

const VALID_WARP_KINDS = new Set<number>([
  WarpKind.Radial,
  WarpKind.Spiral,
  WarpKind.Sinusoidal,
  WarpKind.LegacyPhase0,
  WarpKind.Field,
]);

const VALID_TARGETS = new Set<string>(MODULATION_TARGETS);
const VALID_SOURCES = new Set<string>(AUDIO_SOURCES);
const VALID_TRANSFORMS = new Set<string>(TRANSFORM_KINDS);
const VALID_GENERATORS = new Set<string>(WARP_FIELD_GENERATORS);

export function validatePreset(p: VisualizerPreset): void {
  const issues: string[] = [];

  if (!p.id || typeof p.id !== 'string') issues.push('missing id');
  if (!p.name || typeof p.name !== 'string') issues.push('missing name');

  if (!p.seed || p.seed.kind !== 'waveform') {
    issues.push(`seed.kind must be 'waveform' (got ${JSON.stringify(p.seed?.kind)})`);
  }

  if (!p.warp) {
    issues.push('missing warp config');
  } else if (!VALID_WARP_KINDS.has(p.warp.kind)) {
    issues.push(
      `unknown warp.kind ${p.warp.kind}; valid: ${[...VALID_WARP_KINDS].join(', ')}`,
    );
  }

  if (!p.transition || typeof p.transition.durationMs !== 'number') {
    issues.push('missing transition.durationMs');
  }

  if (!Array.isArray(p.modulation)) {
    issues.push('modulation must be an array');
  } else {
    p.modulation.forEach((b, i) => {
      issues.push(...validateBinding(b, i));
    });
  }

  if (p.warpField) {
    issues.push(...validateWarpField(p.warpField));
    if (p.warp?.kind !== WarpKind.Field) {
      issues.push(
        'warpField is set but warp.kind is not WarpKind.Field. ' +
          'The field is only sampled when the warp kind is Field; either ' +
          'change warp.kind to Field or remove warpField.',
      );
    }
  } else if (p.warp?.kind === WarpKind.Field) {
    issues.push(
      'warp.kind is Field but no warpField config is provided. ' +
        'A Field-kind preset must declare a warpField.',
    );
  }

  if (issues.length > 0) {
    throw new PresetValidationError(p.id ?? '<unknown>', issues);
  }
}

function validateWarpField(f: WarpFieldConfig): string[] {
  const issues: string[] = [];
  if (!VALID_GENERATORS.has(f.generator as string)) {
    issues.push(
      `warpField.generator "${f.generator}" is unknown. Valid: ${WARP_FIELD_GENERATORS.join(', ')}`,
    );
  }
  if (typeof f.seed !== 'number' || !Number.isFinite(f.seed)) {
    issues.push('warpField.seed must be a finite number');
  }
  if (typeof f.strength !== 'number' || !Number.isFinite(f.strength)) {
    issues.push('warpField.strength must be a finite number');
  }
  if (!f.adoption) {
    issues.push('warpField.adoption is required');
  } else {
    const a = f.adoption;
    if (typeof a.beatThreshold !== 'number' || a.beatThreshold < 0 || a.beatThreshold > 1) {
      issues.push('warpField.adoption.beatThreshold must be in [0, 1]');
    }
    if (typeof a.cooldownMs !== 'number' || a.cooldownMs < 0) {
      issues.push('warpField.adoption.cooldownMs must be >= 0');
    }
    if (typeof a.crossfadeMs !== 'number' || a.crossfadeMs <= 0) {
      issues.push('warpField.adoption.crossfadeMs must be > 0');
    }
  }
  return issues;
}

function validateBinding(b: ModulationBinding, idx: number): string[] {
  const issues: string[] = [];
  const at = `modulation[${idx}]`;
  if (!isValidTarget(b.target)) {
    issues.push(
      `${at}: unknown target "${b.target}". Valid: ${MODULATION_TARGETS.join(', ')}`,
    );
  }
  if (!isValidSource(b.source)) {
    issues.push(
      `${at}: unknown source "${b.source}". Valid: ${AUDIO_SOURCES.join(', ')}`,
    );
  }
  if (!isValidTransform(b.transform)) {
    issues.push(
      `${at}: unknown transform "${b.transform}". Valid: ${TRANSFORM_KINDS.join(', ')}`,
    );
  }
  if (typeof b.amount !== 'number' || !Number.isFinite(b.amount)) {
    issues.push(`${at}: amount must be a finite number (got ${b.amount})`);
  }
  return issues;
}

function isValidTarget(t: unknown): t is ModulationTarget {
  return typeof t === 'string' && VALID_TARGETS.has(t);
}
function isValidSource(s: unknown): s is AudioSource {
  return typeof s === 'string' && VALID_SOURCES.has(s);
}
function isValidTransform(t: unknown): t is TransformKind {
  return typeof t === 'string' && VALID_TRANSFORMS.has(t);
}
